import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import StoredMessageInterface from "@/interfaces/inbox/storedMessage.interface";
import { useChatsStore } from "@/store/useChatsStore";
import { InboxModuleType } from "@/types/inboxModule.type";
import { computed, reactive, toRefs } from "@vue/composition-api";

interface RootState {
    storedMessages: StoredMessageInterface[];
    loading: boolean;
    error: boolean;
}

const state: RootState = reactive({
    storedMessages: [],
    loading: false,
    error: false,
});

export default function useStoredMessages() {
    const fetchStoredMessages = () => {
        state.loading = true;
        state.storedMessages = JSON.parse(localStorage.getItem("storedMessages") ?? "[]").filter(
            (item: StoredMessageInterface) => item.inboxModuleType
        );
        state.loading = false;
    };

    const removeStoredMessage = ({ chatId, inboxModuleType }: Omit<StoredMessageInterface, "message">): void => {
        const storedMessagesIndex = state.storedMessages?.findIndex(
            (item) => item.chatId === chatId && item.inboxModuleType === inboxModuleType
        );

        if (storedMessagesIndex !== -1) {
            state.storedMessages.splice(storedMessagesIndex, 1);
            localStorage.setItem("storedMessages", JSON.stringify(state.storedMessages));
        }
    };

    const setStoredMessages = (itemToAdd: StoredMessageInterface): void => {
        // Clear all values that do not have an inboxModuleType, as those entries are legacy data.
        const localStorageValue: StoredMessageInterface[] = JSON.parse(
            localStorage.getItem("storedMessages") ?? "[]"
        ).filter((item: StoredMessageInterface) => item.inboxModuleType);

        const storedMessagesIndex = localStorageValue.findIndex(
            (item: StoredMessageInterface) =>
                item.chatId === itemToAdd.chatId && item.inboxModuleType === itemToAdd.inboxModuleType
        );

        if (storedMessagesIndex !== -1) {
            // Delete old value
            localStorageValue.splice(storedMessagesIndex, 1);
        }

        if (itemToAdd.message.length > 0) {
            localStorageValue.push(itemToAdd);
        }

        if (localStorageValue.length > 0) {
            localStorage.setItem("storedMessages", JSON.stringify(localStorageValue));
        } else {
            localStorage.removeItem("storedMessages");
        }

        state.storedMessages = localStorageValue;
    };

    const messagingStoredMessages = computed(() =>
        state.storedMessages.filter((item) => item.inboxModuleType === InboxModuleType.MESSAGING)
    );
    const emailStoredMessages = computed(() =>
        state.storedMessages.filter((item) => item.inboxModuleType === InboxModuleType.EMAIL)
    );

    const activeChatMessage = computed(() => {
        const { inboxModuleType } = useInboxModuleType();
        const chatsStore = useChatsStore();

        if (inboxModuleType.value === InboxModuleType.EMAIL) {
            return emailStoredMessages.value.find((msg) => msg.chatId === chatsStore.activeChat?.chat.id);
        }

        return messagingStoredMessages.value.find((msg) => msg.chatId === chatsStore.activeChat?.chat.id);
    });

    const getStoredMessage = ({ chatId, inboxModuleType }: Omit<StoredMessageInterface, "message">) => {
        return state.storedMessages?.find((item) => item.chatId === chatId && item.inboxModuleType === inboxModuleType);
    };

    return {
        ...toRefs(state),
        activeChatMessage,
        emailStoredMessages,
        fetchStoredMessages,
        getStoredMessage,
        messagingStoredMessages,
        removeStoredMessage,
        setStoredMessages,
    };
}
